import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import AspectRatio from 'components/utils/AspectRatio'
import { useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import Image from 'components/Common/Image/Image'

export enum OfficeLocations {
  MELBOURNE = 'Melbourne',
  CHADSTONE = 'Chadstone',
  SYDNEY = 'Sydney',
  BONDI = 'Bondi',
  SINGAPORE = 'Singapore',
}

type OfficeLocationMessageKey = 'melbourne' | 'chadstone' | 'sydney' | 'bondi' | 'singapore'

interface OfficeLocationData {
  location: OfficeLocations
  messageKey: OfficeLocationMessageKey
  imageId: string
}

export interface OfficeLocationContent {
  location: OfficeLocations
  displayLocation: string
  officeTitle: string
  address: string
  imageId: string
}

const OFFICE_LOCATION_DATA: Array<OfficeLocationData> = [
  {
    location: OfficeLocations.MELBOURNE,
    messageKey: 'melbourne',
    imageId: 'q1tf7pylqjcf8a67j8ik',
  },
  {
    location: OfficeLocations.CHADSTONE,
    messageKey: 'chadstone',
    imageId: 'stfnt9kfcmt08sq4hbcj',
  },
  {
    location: OfficeLocations.SYDNEY,
    messageKey: 'sydney',
    imageId: '8an3e7vdvcrmwgu2z5qi',
  },
  {
    location: OfficeLocations.BONDI,
    messageKey: 'bondi',
    imageId: '63y27jyk5sop54l6mxb7l',
  },
  {
    location: OfficeLocations.SINGAPORE,
    messageKey: 'singapore',
    imageId: 'c2hnjcrf99m4xjrs16om',
  },
]

const officeLocationLabelMessages = defineMessages({
  melbourne: {
    id: 'jci_-bIvL-viqb6WjtMw',
    defaultMessage: 'Melbourne',
  },
  chadstone: {
    id: 'VuY8yRlAsIoptTQ1kd8A',
    defaultMessage: 'Chadstone',
  },
  sydney: {
    id: 'H-F6X7W808WhThfBhP4x',
    defaultMessage: 'Sydney',
  },
  bondi: {
    id: 'lPEyrstXrWl2EFF-rsLM',
    defaultMessage: 'Bondi',
  },
  singapore: {
    id: 'mAHkfkH85lTHpWyWcj6Z',
    defaultMessage: 'Singapore',
  },
})

const officeTitleMessages = defineMessages({
  melbourne: {
    id: 'y1a1pzC6iizIlkalUzDt',
    defaultMessage: 'Melbourne Office',
  },
  chadstone: {
    id: 'KBGGm5jloH6WN7cQSnPm',
    defaultMessage: 'Chadstone Retail Store',
  },
  sydney: {
    id: 'tsTO4OTCaIu6Rr1uPYQr',
    defaultMessage: 'Sydney Office',
  },
  bondi: {
    id: 'QQnHvofIZHTpVDNXma3e',
    defaultMessage: 'Westfield Bondi Junction Retail Store',
  },
  singapore: {
    id: 'Outc2szPW9lcnAZmVodC',
    defaultMessage: 'Singapore Office',
  },
})

const officeAddressMessages = defineMessages({
  melbourne: {
    id: 'dmJuoiVQ0ACU595H0zJJ',
    defaultMessage: 'Level 4, 68 Clarke Street, Southbank, VIC 3006, Australia',
  },
  chadstone: {
    id: 'nvPuOKiqrhQgPFroSwN9',
    defaultMessage: 'Chadstone Shopping Centre - Lower Ground, 1341 Dandenong Rd, Malvern East VIC 3148',
  },
  sydney: {
    id: '_w5GcKQKC96KLYmI2juW',
    defaultMessage: 'Level 28, 201 Kent Street, Sydney 2000, NSW, Australia',
  },
  bondi: {
    id: 'AQecZhZZ0ie-vCD1IlN9',
    defaultMessage: 'Ground Floor, Oxford Street entrance at Bondi Junction (Shop 3042 & 3043)',
  },
  singapore: {
    id: '33jSI9a4QrUiGvVDwT4f',
    defaultMessage: '27B Teo Hong Road, Singapore 088334',
  },
})

export function useOfficeLocationContent(): {
  offices: Array<OfficeLocationContent>
  officeContentByLocation: Record<OfficeLocations, OfficeLocationContent>
} {
  const intl = useIntl()

  return useMemo(() => {
    const offices = OFFICE_LOCATION_DATA.map((office) => ({
      location: office.location,
      displayLocation: intl.formatMessage(officeLocationLabelMessages[office.messageKey]),
      officeTitle: intl.formatMessage(officeTitleMessages[office.messageKey]),
      address: intl.formatMessage(officeAddressMessages[office.messageKey]),
      imageId: office.imageId,
    }))

    const officeContentByLocation = offices.reduce<Record<OfficeLocations, OfficeLocationContent>>((acc, office) => {
      acc[office.location] = office
      return acc
    }, {} as Record<OfficeLocations, OfficeLocationContent>)

    return {
      offices,
      officeContentByLocation,
    }
  }, [intl])
}

interface Props {
  selectedOffice: OfficeLocations
}

function OfficeLocationModal(props: Props) {
  const { selectedOffice } = props
  const { offices, officeContentByLocation } = useOfficeLocationContent()
  const selectedOfficeContent = officeContentByLocation[selectedOffice] ?? offices[0]

  return (
    <ModalBase>
      <ModalHeader title={selectedOfficeContent.officeTitle} subtitle={selectedOfficeContent.address} />
      <AspectRatio ratio="2:1" tabletRatio="16:8">
        <Image id={selectedOfficeContent.imageId} fit="center" aspectRatio="2:1" width={720} loading="lazy" />
      </AspectRatio>
    </ModalBase>
  )
}

export default OfficeLocationModal
